// Three Singapore-style property calculators for the Resources / Calculators page.
//
// 1. Mortgage Calculator        — monthly repayment, total interest
// 2. Sales Proceeds Calculator  — net cash after loan, CPF refund, SSD, fees
// 3. Purchase Calculator        — total upfront cash + CPF + BSD + ABSD + monthly repayment
//
// Singapore IRAS 2023+ tiered Buyer's Stamp Duty and Apr-2023 ABSD rates.

import { useEffect, useMemo, useState } from "react";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";

export function formatSGD(num) {
  if (Number.isNaN(num) || !Number.isFinite(num)) return "S$ 0";
  return new Intl.NumberFormat("en-SG", {
    style: "currency",
    currency: "SGD",
    maximumFractionDigits: 0,
  }).format(num);
}

export function Field({ label, value, onChange, placeholder, suffix, help, readOnly = false, highlight = false, testId }) {
  return (
    <label className="block">
      <span className="editorial-label">{label}</span>
      <div
        className={`relative ${highlight ? "border border-dashed border-bronze/60 bg-cream/60 px-3" : ""}`}
      >
        <input
          type="text"
          inputMode="decimal"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          readOnly={readOnly}
          data-testid={testId}
          className={`editorial-input pr-12 ${highlight ? "border-b-0" : ""} ${readOnly ? "text-bronze font-serif text-lg" : ""}`}
        />
        {suffix && (
          <span className="absolute right-2 bottom-3 text-xs text-olive">{suffix}</span>
        )}
      </div>
      {help && (
        <span className="block mt-1.5 text-[0.7rem] text-olive/80 leading-snug">{help}</span>
      )}
    </label>
  );
}

export function ResultRow({ label, value, big = false, testId }) {
  return (
    <div className="flex items-baseline justify-between py-3 border-b border-bronze/15 last:border-b-0">
      <span className="text-[0.7rem] tracking-[0.22em] uppercase text-olive">{label}</span>
      <span
        data-testid={testId}
        className={`font-serif text-ink ${big ? "text-3xl md:text-4xl" : "text-xl"}`}
      >
        {value}
      </span>
    </div>
  );
}

export function Disclaimer() {
  return (
    <p className="mt-6 text-[0.7rem] tracking-[0.18em] uppercase text-olive/70">
      Estimates only. Final figures depend on bank approval, valuation & individual circumstances.
    </p>
  );
}

/* ------------------------------------------------------------------ *
 * 1. Mortgage Calculator                                              *
 * ------------------------------------------------------------------ */
export function MortgageCalculator() {
  const [price, setPrice] = useState("1500000");
  const [downPct, setDownPct] = useState("25");
  const [rate, setRate] = useState("3.5");
  const [years, setYears] = useState("25");

  const calc = useMemo(() => {
    const p = parseFloat(price) || 0;
    const dp = (parseFloat(downPct) || 0) / 100;
    const loan = p * (1 - dp);
    const r = (parseFloat(rate) || 0) / 100 / 12;
    const n = (parseFloat(years) || 0) * 12;
    let monthly = 0;
    if (r > 0 && n > 0) monthly = (loan * r) / (1 - Math.pow(1 + r, -n));
    else if (n > 0) monthly = loan / n;
    const totalInterest = Math.max(0, monthly * n - loan);
    return { loan, monthly, totalInterest, downpayment: p * dp, totalCost: monthly * n };
  }, [price, downPct, rate, years]);

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-6">
        <h3 className="font-serif text-3xl text-ink">Mortgage Calculator</h3>
        <p className="text-sm text-olive -mt-4">
          Estimate your monthly repayment and total interest over the life of the loan.
        </p>
        <Field label="Property Price" value={price} onChange={setPrice} placeholder="1,500,000" suffix="S$" testId="calc-mortgage-price" />
        <Field label="Downpayment" value={downPct} onChange={setDownPct} placeholder="25" suffix="%" testId="calc-mortgage-down" />
        <Field label="Interest Rate" value={rate} onChange={setRate} placeholder="3.5" suffix="% p.a." testId="calc-mortgage-rate" />
        <Field label="Loan Tenure" value={years} onChange={setYears} placeholder="25" suffix="years" testId="calc-mortgage-years" />
      </div>
      <div className="bg-beige/60 p-8">
        <div className="eyebrow mb-6">Mortgage Breakdown</div>
        <ResultRow label="Downpayment" value={formatSGD(calc.downpayment)} testId="calc-mortgage-downpayment" />
        <ResultRow label="Loan Amount" value={formatSGD(calc.loan)} testId="calc-mortgage-loan" />
        <ResultRow label="Monthly Repayment" value={formatSGD(calc.monthly)} big testId="calc-mortgage-monthly" />
        <ResultRow label="Total Interest Paid" value={formatSGD(calc.totalInterest)} testId="calc-mortgage-total-interest" />
        <ResultRow label="Total Cost of Loan" value={formatSGD(calc.totalCost)} testId="calc-mortgage-total-cost" />
        <Disclaimer />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 2. Sales Proceeds Calculator                                        *
 * ------------------------------------------------------------------ */
function ssdRateFromYears(y) {
  if (y < 1) return { rate: 0.12, label: "Held < 1 year — 12% SSD" };
  if (y < 2) return { rate: 0.08, label: "Held 1–2 years — 8% SSD" };
  if (y < 3) return { rate: 0.04, label: "Held 2–3 years — 4% SSD" };
  return { rate: 0, label: "Held more than 3 years — no SSD payable." };
}

export function SalesProceedsCalculator() {
  const [price, setPrice] = useState("1200000");
  const [loan, setLoan] = useState("400000");
  const [cpfTotal, setCpfTotal] = useState("250000");
  const [holding, setHolding] = useState("4");
  const [agentFee, setAgentFee] = useState("24000");
  const [agentManual, setAgentManual] = useState(false);
  const [legalFee, setLegalFee] = useState("3000");
  const [otherCosts, setOtherCosts] = useState("0");

  // Auto-fill agent fee at 2% of selling price unless user overrides
  useEffect(() => {
    if (!agentManual) {
      const p = parseFloat(price) || 0;
      setAgentFee(String(Math.round(p * 0.02)));
    }
  }, [price, agentManual]);

  const ssd = useMemo(() => {
    const y = parseFloat(holding) || 0;
    const r = ssdRateFromYears(y);
    const p = parseFloat(price) || 0;
    return { ...r, amount: p * r.rate };
  }, [holding, price]);

  const calc = useMemo(() => {
    const p = parseFloat(price) || 0;
    const ln = parseFloat(loan) || 0;
    const cpf = parseFloat(cpfTotal) || 0;
    const af = parseFloat(agentFee) || 0;
    const agentGst = af * 0.09;
    const lf = parseFloat(legalFee) || 0;
    const oc = parseFloat(otherCosts) || 0;
    const totalDeductions = ln + cpf + ssd.amount + af + agentGst + lf + oc;
    const netCash = p - totalDeductions;
    return { agent: af, agentGst, totalDeductions, netCash };
  }, [price, loan, cpfTotal, ssd.amount, agentFee, legalFee, otherCosts]);

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-6">
        <h3 className="font-serif text-3xl text-ink">Sales Proceeds Calculator</h3>
        <p className="text-sm text-olive -mt-4">
          Estimate the cash you will receive after selling your property, including Seller's Stamp Duty based on your holding period.
        </p>
        <Field
          label="Estimated Selling Price" value={price} onChange={setPrice}
          placeholder="1,200,000" suffix="S$" testId="calc-sales-price"
        />
        <Field
          label="Outstanding Loan" value={loan} onChange={setLoan}
          placeholder="400,000" suffix="S$" testId="calc-sales-loan"
        />
        <Field
          label="CPF Used + Accrued Interest" value={cpfTotal} onChange={setCpfTotal}
          placeholder="250,000" suffix="S$"
          help="Will be refunded to your CPF, not cash."
          testId="calc-sales-cpf"
        />
        <Field
          label="Holding Period" value={holding} onChange={setHolding}
          placeholder="4" suffix="years"
          help="Years from purchase to sale. Determines Seller's Stamp Duty (SSD)."
          testId="calc-sales-holding"
        />
        <Field
          label={`Seller's Stamp Duty (SSD) — Auto-calculated · ${(ssd.rate * 100).toFixed(0)}%`}
          value={String(Math.round(ssd.amount))}
          onChange={() => {}}
          readOnly
          highlight
          help={ssd.label}
          testId="calc-sales-ssd"
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field
            label="Agent Fee" value={agentFee}
            onChange={(v) => { setAgentFee(v); setAgentManual(true); }}
            placeholder="24,000" suffix="S$"
            help="Auto: 2% of selling price + 9% GST. Editable."
            testId="calc-sales-agent"
          />
          <Field
            label="Legal Fee" value={legalFee} onChange={setLegalFee}
            placeholder="3,000" suffix="S$"
            help="Default S$3,000. Editable."
            testId="calc-sales-legal"
          />
        </div>
        <Field
          label="Other Costs" value={otherCosts} onChange={setOtherCosts}
          placeholder="0" suffix="S$" testId="calc-sales-other"
        />
        <button
          type="button"
          onClick={() => {
            setPrice("1200000"); setLoan("400000"); setCpfTotal("250000");
            setHolding("4"); setLegalFee("3000"); setOtherCosts("0");
            setAgentManual(false);
          }}
          data-testid="calc-sales-reset"
          className="text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark inline-flex items-center gap-2"
        >
          ↺ Reset
        </button>
      </div>

      <div className="bg-beige/60 p-8">
        <div className="eyebrow mb-6">Sale Breakdown</div>
        <ResultRow label="Selling Price" value={formatSGD(parseFloat(price) || 0)} testId="calc-sales-out-price" />
        <ResultRow label="Less: Outstanding Loan" value={formatSGD(parseFloat(loan) || 0)} testId="calc-sales-deduct-loan" />
        <ResultRow label="Less: CPF Refund (P + Interest)" value={formatSGD(parseFloat(cpfTotal) || 0)} testId="calc-sales-deduct-cpf" />
        <ResultRow label={`Less: SSD · ${(ssd.rate * 100).toFixed(0)}%`} value={formatSGD(ssd.amount)} testId="calc-sales-deduct-ssd" />
        <ResultRow label="Less: Agent Fee + 9% GST" value={formatSGD(calc.agent + calc.agentGst)} testId="calc-sales-deduct-agent" />
        <ResultRow label="Less: Legal Fee" value={formatSGD(parseFloat(legalFee) || 0)} testId="calc-sales-deduct-legal" />
        <ResultRow label="Less: Other Costs" value={formatSGD(parseFloat(otherCosts) || 0)} testId="calc-sales-deduct-other" />
        <div className="h-px bg-bronze/40 my-3" />
        <ResultRow label="Estimated Net Cash" value={formatSGD(calc.netCash)} big testId="calc-sales-net" />
        <p className="mt-4 text-[0.7rem] tracking-[0.18em] uppercase text-olive/70">
          CPF refund returns to your CPF Ordinary Account, not your cash pocket.
        </p>
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ *
 * 3. Purchase Calculator                                              *
 * ------------------------------------------------------------------ */
function computeBSD(price) {
  let bsd = 0;
  let remaining = price;
  const tiers = [
    { up: 180000, rate: 0.01 },
    { up: 180000, rate: 0.02 },
    { up: 640000, rate: 0.03 },
    { up: 500000, rate: 0.04 },
    { up: 1500000, rate: 0.05 },
    { up: Infinity, rate: 0.06 },
  ];
  for (const t of tiers) {
    const slice = Math.min(remaining, t.up);
    if (slice <= 0) break;
    bsd += slice * t.rate;
    remaining -= slice;
  }
  return bsd;
}

const PROFILE_OPTIONS = [
  { v: "SC", l: "Singapore Citizen" },
  { v: "PR", l: "Permanent Resident" },
  { v: "FR", l: "Foreigner" },
];
const PROPERTY_NUM_OPTIONS = [
  { v: "1", l: "First Property" },
  { v: "2", l: "Second Property" },
  { v: "3", l: "Third+ Property" },
];

function getABSDRate(profile, num) {
  if (profile === "FR") return { rate: 0.60, label: "Foreigner (any) · ABSD rate 60%" };
  if (profile === "SC") {
    if (num === "1") return { rate: 0, label: "Singapore Citizen · First Property · ABSD rate 0%" };
    if (num === "2") return { rate: 0.20, label: "Singapore Citizen · Second Property · ABSD rate 20%" };
    return { rate: 0.30, label: "Singapore Citizen · Third+ Property · ABSD rate 30%" };
  }
  // PR
  if (num === "1") return { rate: 0.05, label: "Permanent Resident · First Property · ABSD rate 5%" };
  if (num === "2") return { rate: 0.30, label: "Permanent Resident · Second Property · ABSD rate 30%" };
  return { rate: 0.35, label: "Permanent Resident · Third+ Property · ABSD rate 35%" };
}

export function PurchaseCalculator() {
  const [price, setPrice] = useState("1500000");
  const [ltv, setLtv] = useState("75");
  const [tenure, setTenure] = useState("25");
  const [rate, setRate] = useState("3.5");
  const [cash, setCash] = useState("75000");
  const [cashManual, setCashManual] = useState(false);
  const [propertyNum, setPropertyNum] = useState("1");
  const [profile, setProfile] = useState("SC");

  // Auto-fill cash to 5% of price unless user manually changed it
  useEffect(() => {
    if (!cashManual) {
      const p = parseFloat(price) || 0;
      setCash(String(Math.round(p * 0.05)));
    }
  }, [price, cashManual]);

  const calc = useMemo(() => {
    const p = parseFloat(price) || 0;
    const lt = (parseFloat(ltv) || 0) / 100;
    const minCash = p * 0.05;
    let cashVal = parseFloat(cash) || 0;
    if (cashVal < minCash) cashVal = minCash;          // enforce minimum 5%
    const loan = p * lt;
    const totalDown = p - loan;
    const cpfDown = Math.max(0, totalDown - cashVal);

    const r = (parseFloat(rate) || 0) / 100 / 12;
    const n = (parseFloat(tenure) || 0) * 12;
    let monthly = 0;
    if (r > 0 && n > 0) monthly = (loan * r) / (1 - Math.pow(1 + r, -n));
    else if (n > 0) monthly = loan / n;

    const bsd = computeBSD(p);
    const absdInfo = getABSDRate(profile, propertyNum);
    const absd = p * absdInfo.rate;

    const totalUpfront = totalDown + bsd + absd;
    const cashUpfront = cashVal + bsd + absd;

    return { loan, monthly, totalDown, cashVal, cpfDown, bsd, absd, absdInfo, totalUpfront, cashUpfront };
  }, [price, ltv, tenure, rate, cash, propertyNum, profile]);

  return (
    <div className="grid md:grid-cols-2 gap-10">
      <div className="space-y-6">
        <h3 className="font-serif text-3xl text-ink">Purchase Calculator</h3>
        <p className="text-sm text-olive -mt-4">
          Estimate your total upfront cost — cash, CPF, BSD, ABSD, and monthly repayment — based on your buyer profile.
        </p>

        <Field
          label="Purchase Price" value={price} onChange={(v) => setPrice(v)}
          placeholder="1,500,000" suffix="S$" testId="calc-purchase-price"
        />

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <Field
            label="Loan-to-Value Ratio" value={ltv} onChange={setLtv}
            placeholder="75" suffix="%"
            help="Typically up to 75%"
            testId="calc-purchase-ltv"
          />
          <Field
            label="Loan Tenure" value={tenure} onChange={setTenure}
            placeholder="25" suffix="years" testId="calc-purchase-tenure"
          />
        </div>

        <Field
          label="Interest Rate" value={rate} onChange={setRate}
          placeholder="3.5" suffix="% p.a." testId="calc-purchase-rate"
        />

        <Field
          label="Cash Downpayment" value={cash}
          onChange={(v) => { setCash(v); setCashManual(true); }}
          placeholder="75,000" suffix="S$"
          help="Auto-filled at minimum 5% of purchase price. You can increase it — values below 5% are not allowed."
          testId="calc-purchase-cash"
        />

        <Field
          label="CPF Downpayment (Auto-calculated)"
          value={String(Math.round(calc.cpfDown))} onChange={() => {}}
          readOnly highlight
          suffix="S$"
          help="Total downpayment minus cash. Subject to CPF usage limits."
          testId="calc-purchase-cpf"
        />

        <div className="pt-4 border-t border-bronze/15">
          <div className="eyebrow mb-4">Buyer Profile</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <span className="editorial-label">Buying for</span>
              <Select value={propertyNum} onValueChange={setPropertyNum}>
                <SelectTrigger
                  data-testid="calc-purchase-property-num"
                  className="editorial-input flex justify-between border-0 border-b border-bronze/40 rounded-none focus:ring-0 px-0 h-auto py-3 bg-transparent"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROPERTY_NUM_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v} data-testid={`calc-purchase-property-num-${o.v}`}>
                      {o.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <span className="editorial-label">Buyer Profile</span>
              <Select value={profile} onValueChange={setProfile}>
                <SelectTrigger
                  data-testid="calc-purchase-profile"
                  className="editorial-input flex justify-between border-0 border-b border-bronze/40 rounded-none focus:ring-0 px-0 h-auto py-3 bg-transparent"
                >
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PROFILE_OPTIONS.map((o) => (
                    <SelectItem key={o.v} value={o.v} data-testid={`calc-purchase-profile-${o.v}`}>
                      {o.l}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        <Field
          label="Buyer's Stamp Duty (BSD) — Auto-calculated"
          value={String(Math.round(calc.bsd))} onChange={() => {}}
          readOnly highlight suffix="S$"
          help="Tiered BSD based on purchase price."
          testId="calc-purchase-bsd"
        />
        <Field
          label="Additional Buyer's Stamp Duty (ABSD) — Auto-calculated"
          value={String(Math.round(calc.absd))} onChange={() => {}}
          readOnly highlight suffix="S$"
          help={calc.absdInfo.label}
          testId="calc-purchase-absd"
        />

        <button
          type="button"
          onClick={() => {
            setPrice("1500000"); setLtv("75"); setTenure("25"); setRate("3.5");
            setCashManual(false); setPropertyNum("1"); setProfile("SC");
          }}
          data-testid="calc-purchase-reset"
          className="text-[0.7rem] tracking-[0.22em] uppercase text-bronze hover:text-bronze_dark inline-flex items-center gap-2"
        >
          ↺ Reset
        </button>
      </div>

      <div className="bg-beige/60 p-8">
        <div className="eyebrow mb-6">Upfront Costs</div>
        <ResultRow label="Loan Amount" value={formatSGD(calc.loan)} testId="calc-purchase-loan" />
        <ResultRow label="Total Downpayment" value={formatSGD(calc.totalDown)} testId="calc-purchase-down-total" />
        <ResultRow label="— Cash" value={formatSGD(calc.cashVal)} testId="calc-purchase-down-cash" />
        <ResultRow label="— CPF" value={formatSGD(calc.cpfDown)} testId="calc-purchase-down-cpf" />
        <ResultRow label="Buyer's Stamp Duty (BSD)" value={formatSGD(calc.bsd)} testId="calc-purchase-bsd-out" />
        <ResultRow label={`ABSD · ${(calc.absdInfo.rate * 100).toFixed(0)}%`} value={formatSGD(calc.absd)} testId="calc-purchase-absd-out" />
        <div className="h-px bg-bronze/40 my-3" />
        <ResultRow label="Cash Required Upfront" value={formatSGD(calc.cashUpfront)} big testId="calc-purchase-cash-upfront" />
        <ResultRow label="Total Upfront (Cash + CPF)" value={formatSGD(calc.totalUpfront)} testId="calc-purchase-total-upfront" />
        <div className="h-px bg-bronze/40 my-3" />
        <ResultRow label="Monthly Repayment" value={formatSGD(calc.monthly)} testId="calc-purchase-monthly" />
        <Disclaimer />
      </div>
    </div>
  );
}
